import {
  FormControl,
  ListSubheader,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Typography,
} from '@mui/material'
import { BarcodeInspectionType } from '@ulysses-inc/harami_api_client'
import { type FC, useCallback, useMemo } from 'react'
import { useAreaFormContext } from './AreaFormContext'
import {
  AREA_FORM_DEFAULT_VALUES,
  AreaFormValues,
  AreaType,
} from './AreaFormContext/useAreaForm'

type Group = {
  id: string
  label: string
  options: { label: string; value: string }[]
}

const matchingTypeGroups: Group[] = [
  {
    id: 'imageMatching',
    label: '画像マッチング',
    options: [{ label: '見た目が一致していることを判定', value: 'pattern' }],
  },
  {
    id: 'textMatching',
    label: 'テキストマッチング',
    options: [
      { label: '固定の英数字と一致していることを判定', value: 'alphanumeric' },
      {
        label: 'レポートアプリで入力したテキスト(英数字)との一致を判定',
        value: 'alphanumericInput',
      },
    ],
  },
  {
    id: 'expiration',
    label: '日付',
    options: [
      {
        label: 'レポートアプリで検査したときを基準日時にする',
        value: 'baseTime',
      },
      {
        label: 'レポートアプリで基準日時を選択する',
        value: 'baseTimeSelectAtInspection',
      },
      {
        label:
          'レポートアプリで検査したときを基準日時にして、以下より未来かを判定',
        value: 'futureBasedOnInspectionTime',
      },
      {
        label: 'レポートアプリで基準日時を選択して、以下より未来かを判定',
        value: 'futureBasedOnSelectedTime',
      },
    ],
  },
  {
    id: 'barcode',
    label: 'バーコード',
    options: [
      { label: 'バーコードの一致を判定', value: 'barcodeMatch' },
      { label: 'バーコードが読み取れるかを判定', value: 'barcodeRead' },
    ],
  },
]

export const AreaTypeSelect: FC = () => {
  const { formValues, setValue, preservedSettings } = useAreaFormContext()

  const value = useMemo(() => {
    if (formValues.type === 'expiration') {
      return formValues.expirationLogic ?? 'baseTime'
    }
    if (formValues.type === 'barcode') {
      return formValues.barcodeInspectionType === BarcodeInspectionType.READ
        ? 'barcodeRead'
        : 'barcodeMatch'
    }
    return formValues.type
  }, [
    formValues.type,
    formValues.expirationLogic,
    formValues.barcodeInspectionType,
  ])

  // TODO: handleChange では、選択されたオプションに応じて、formの値と上手く対応させているため、かなり複雑になっているため、リファクタリングを行う
  const handleChange = useCallback(
    (e: SelectChangeEvent<string>) => {
      const selected = e.target.value
      const group = matchingTypeGroups.find(g =>
        g.options.some(o => o.value === selected),
      )

      if (group?.id === 'expiration') {
        setValue('type', 'expiration')
        setValue(
          'expirationLogic',
          selected as AreaFormValues['expirationLogic'],
        )
        if (preservedSettings) {
          setValue('expirationYear', preservedSettings.expirationYear)
          setValue('expirationMonth', preservedSettings.expirationMonth)
          setValue('expirationDay', preservedSettings.expirationDay)
          setValue('expirationHour', preservedSettings.expirationHour)
          setValue(
            'expirationNotationOrder',
            preservedSettings.expirationNotationOrder,
          )
        }
        setValue(
          'expirationDuration',
          AREA_FORM_DEFAULT_VALUES.expirationDuration,
        )
      } else if (group?.id === 'barcode') {
        setValue('type', 'barcode')
        setValue(
          'barcodeInspectionType',
          selected === 'barcodeRead'
            ? BarcodeInspectionType.READ
            : BarcodeInspectionType.MATCH,
        )
        setValue('barcodeValue', AREA_FORM_DEFAULT_VALUES.barcodeValue)
      } else {
        const newType = selected as AreaType
        setValue('type', newType)
        setValue(
          'alphanumericValue',
          AREA_FORM_DEFAULT_VALUES.alphanumericValue,
        )
        setValue('barcodeValue', AREA_FORM_DEFAULT_VALUES.barcodeValue)
      }
    },
    [setValue, preservedSettings],
  )

  return (
    <FormControl sx={{ gap: 0.5 }} required>
      <Typography
        component="label"
        sx={{
          color: 'text.primary',
          fontSize: 14,
          fontWeight: 'bold',
        }}
      >
        一致タイプ
      </Typography>
      <Select
        fullWidth
        value={value}
        onChange={handleChange}
        sx={{ backgroundColor: 'white' }}
      >
        {matchingTypeGroups.map(group => [
          <ListSubheader key={group.id}>{group.label}</ListSubheader>,
          ...group.options.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          )),
        ])}
      </Select>
    </FormControl>
  )
}
